import React, { useState, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from 'redux/store';
import Image from 'components/image/Image';
import useLocales from 'hooks/useLocales';
import OurBenefits from 'components/ourBenefits';
import { CMSService } from 'services/cms/cms';
import { IMe } from 'core/interface/me';
import { callForAllElement } from 'helpers/callAll';
import { useParams } from 'react-router';
import PopUpWrapper from 'components/PopUpWrapper';
import HtmlContent from 'components/HtmlContent';

function Benefit({ canEdit }: { canEdit: boolean }) {
  const { t } = useLocales();
  const [openModal, setOpenModal] = useState(false);
  const { cms } = useSelector((state: RootState) => state.cms);
  const me = useSelector((state: IMe) => state.user).user;
  const { id } = useParams();

  const handleModal = () => {
    setOpenModal(!openModal);
  };

  const handleChange = (companyData: any) => {
    const { benefit_section_title, benefit_section_description } = companyData;
    const formData = new FormData();
    benefit_section_title &&
      formData.append('benefit_section_title', benefit_section_title);
    benefit_section_description &&
      formData.append(
        'benefit_section_description',
        benefit_section_description
      );
    CMSService.updateCMS(me?.company, formData);
    handleModal();
  };

  const handleBenefitChange = (data: any, isNew: boolean) => {
    if (isNew) {
      callForAllElement(data, (benefit) => {
        return CMSService.updateCMSBenefits(me?.company, benefit);
      });
    } else {
      callForAllElement(data, (benefit) => {
        return CMSService.updateCMSBenefit(
          benefit.page,
          benefit.changedFields,
          benefit.id
        );
      });
    }
    handleModal();
  };

  // Calculate grid columns: 3 columns if divisible by 3, otherwise 2 columns
  const gridColumns = useMemo(() => {
    const benefitsCount = cms?.benefits?.length || 0;

    // If no benefits, default to 2 columns
    if (benefitsCount === 0) return 2;

    // If benefits count is divisible by 3, use 3 columns
    return benefitsCount % 3 === 0 ? 3 : 2;
  }, [cms?.benefits]);

  return (
    <div className="benefit_container">
      <div className="benefit_container_header">
        <div className="benefitsContainer">
          <div>
            <h2 className="benefit_section_title">
              {cms?.benefit_section_title}
            </h2>
            <h1>{t('Our benefits and why you should work with us')}</h1>
          </div>
          {canEdit && (
            <div className="pencil_icon" onClick={handleModal}>
              <div className="pencil_circle" />
              <Image
                className="pencil"
                name="pencil.svg"
                width={13}
                height={13}
              />
            </div>
          )}
        </div>

        <div className="benefitsDescriptionContainer">
          <div className="benefitsDescription">
            {cms?.benefit_section_description && (
              <HtmlContent content={cms.benefit_section_description} />
            )}
          </div>
        </div>
      </div>
      <div
        className="benefitsCartContainer"
        style={{
          gridTemplateColumns: `repeat(${gridColumns}, 1fr)`,
          gridTemplateRows: `repeat(${Math.ceil(
            (cms?.benefits?.length || 0) / gridColumns
          )}, auto)`
        }}
      >
        {cms?.benefits?.map((e: any) => (
          <div key={e.id} className="benefitsCart">
            <Image
              className="checked"
              name="checkedBenefits.svg"
              width={40}
              height={40}
            />

            <div>
              <p className="benefitsTitle">{e.title}</p>
              <p className="benefitsDescription">{e.description}</p>
            </div>
          </div>
        ))}
      </div>
      {openModal && (
        <PopUpWrapper onClick={handleModal}>
          <OurBenefits
            companyData={cms}
            setBenefitData={handleBenefitChange}
            setNewData={handleChange}
            isOneBtn={true}
            onClose={handleModal}
            showTopSide={false}
            isPopup={true}
            showTitle
          />
        </PopUpWrapper>
      )}
    </div>
  );
}

export default Benefit;
